import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button';
import BasicInput from '@/components/BasicInput';
import Select from '@/components/Select';
import ConfirmDialog from '@/components/ConfirmDialog';
import { MAX_ADDRESSES } from '@/utils/constants';
import markedIcon from '@/assets/marked.svg';
import { validateAddressForm } from '@/utils/addressValidation';
import { getAddressByPostalCode } from '@/api/address';
import { useMetadata } from '@/hooks/useMetadata';
import { trackEvent } from '@/utils/googleAnalytics';

const AddressSection = ({ onComplete, onEditingChange, onEditRequest, addresses = [], finalSelectedAddressId }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(!addresses.length);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(!addresses.length);
  const [formData, setFormData] = useState({ prefecture: '' });
  const [errors, setErrors] = useState({});
  const [addressFieldsChanged, setAddressFieldsChanged] = useState(false);
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);

  // Use shared metadata hook - data is prefetched on app start and cached for 1 hour
  const { data: metadata, isLoading: isLoadingPrefectures } = useMetadata();
  
  // Transform prefectures data to match expected format
  const prefectures = metadata?.prefectures?.map(prefecture => ({
    value: prefecture.name,
    label: prefecture.name
  })) || [];

  // Notify parent about editing state changes using the dedicated callback
  useEffect(() => {
    onEditingChange && onEditingChange(isEditing);
  }, [isEditing, onEditingChange]);

  useEffect(() => {
    if (finalSelectedAddressId) {
      setSelectedAddressId(finalSelectedAddressId);
      setIsEditing(false);
    } else {
      setIsCreatingNew(true);
      setIsEditing(true);
    }
  }, [finalSelectedAddressId]);

  // Auto-select default address when addresses are loaded
  useEffect(() => {
    if (addresses.length > 0 && selectedAddressId) {
      const selectedAddress = addresses.find(a => a.id === selectedAddressId);
      // might be null before the data is fetched
      if (selectedAddress) {
        setFormData({
          firstName: selectedAddress.firstname,
          lastName: selectedAddress.lastname,
          firstNameKana: selectedAddress.firstnameKana,
          lastNameKana: selectedAddress.lastnameKana,
          phone: selectedAddress.contactNumber,
          postalCode: selectedAddress.postalCode,
          prefecture: selectedAddress.prefecture,
          city: selectedAddress.city,
          street: selectedAddress.address1,
          building: selectedAddress.address2 || '',
        });
        setIsCreatingNew(false);
      }
    }
  }, [addresses, selectedAddressId]);

  const handleInputChange = (field, value) => {
    // Special handling for postal code
    if (field === 'postalCode') {
      // Remove any non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Format as XXX-XXXX
      if (digitsOnly.length <= 3) {
        value = digitsOnly;
      } else {
        value = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 7)}`;
      }
    }
    
    // Special handling for phone number
    if (field === 'phone') {
      // Remove any non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Format strictly as 0XX-XXXX-XXXX
      if (digitsOnly.length <= 3) {
        value = digitsOnly;
      } else if (digitsOnly.length <= 7) {
        value = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
      } else {
        value = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 7)}-${digitsOnly.slice(7, 11)}`;
      }
      
      // Limit to 11 digits max
      if (digitsOnly.length > 11) {
        value = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 7)}-${digitsOnly.slice(7, 11)}`;
      }
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Set address fields changed flag when editing postal code, prefecture, city, street, or building
    if (['postalCode', 'prefecture', 'city', 'street', 'building'].includes(field)) {
      setAddressFieldsChanged(true);
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const isFormModified = (currentFormData, selectedAddress) => {
    if (!selectedAddress) return true;
    
    return (
      currentFormData.firstName != selectedAddress.firstname ||
      currentFormData.lastName != selectedAddress.lastname ||
      currentFormData.firstNameKana != selectedAddress.firstnameKana ||
      currentFormData.lastNameKana != selectedAddress.lastnameKana ||
      currentFormData.phone != selectedAddress.contactNumber ||
      currentFormData.postalCode != selectedAddress.postalCode ||
      currentFormData.prefecture != selectedAddress.prefecture ||
      currentFormData.city != selectedAddress.city ||
      currentFormData.street != selectedAddress.address1 ||
      (currentFormData.building || '') != (selectedAddress.address2 || '')
    );
  };

  const requiredFields = [
    'lastName', 'firstName', 'lastNameKana', 'firstNameKana',
    'phone', 'postalCode', 'prefecture', 'city', 'street'
  ];
  
  const isFormAllFilled = requiredFields.every(field => formData[field]?.trim());

  const handleContinue = () => {
    trackEvent('address_save_click');

    // Validate form data
    const { isValid, errors: validationErrors } = validateAddressForm(formData);

    if (!isValid) {
      // Translate error messages
      const translatedErrors = {};
      Object.keys(validationErrors).forEach(key => {
        translatedErrors[key] = t(validationErrors[key]);
      });
      setErrors(translatedErrors);
      return;
    }

    if (addresses.length > 0 && !isCreatingNew) {
      const selectedAddress = addresses.find(a => a.id === selectedAddressId);
      
      if (!isFormModified(formData, selectedAddress)) {
        setIsEditing(false);
        // Pass only the address ID when using an existing address
        onComplete({ addressId: selectedAddressId });
        return;
      }
    }

    // Disallow modification when max number is reached because it will trigger an address creationg
    if (addresses.length >= MAX_ADDRESSES) {
      setIsLimitDialogOpen(true);
      return;
    }

    setIsEditing(false);
    // Pass the full form data when creating a new address
    onComplete(formData);
  };

  const handleAddressSelect = (addressId) => {
    if (addressId === 'new') {
      if (addresses.length >= MAX_ADDRESSES) {
        setIsLimitDialogOpen(true);
        return;
      }
      
      setIsCreatingNew(true);
      setFormData({
        firstName: '',
        lastName: '',
        firstNameKana: '',
        lastNameKana: '',
        phone: '',
        postalCode: '',
        prefecture: '',
        city: '',
        street: '',
        building: '',
      });
      return;
    }

    setIsCreatingNew(false);
    const selectedAddress = addresses.find(a => a.id === addressId);
    if (selectedAddress) {
      setSelectedAddressId(addressId);
      setFormData({
        firstName: selectedAddress.firstname,
        lastName: selectedAddress.lastname,
        firstNameKana: selectedAddress.firstnameKana,
        lastNameKana: selectedAddress.lastnameKana,
        phone: selectedAddress.contactNumber,
        postalCode: selectedAddress.postalCode,
        prefecture: selectedAddress.prefecture,
        city: selectedAddress.city,
        street: selectedAddress.address1,
        building: selectedAddress.address2 || '',
      });
    }
  };

  const handleLimitDialogClose = () => {
    setIsLimitDialogOpen(false);
  };

  const handleAutoFill = async () => {
    if (!formData.postalCode || formData.postalCode.length < 7) {
      setErrors(prev => ({
        ...prev,
        postalCode: t('purchase.address.postal_code_invalid')
      }));
      return;
    }

    setIsAutoFilling(true);
    try {
      const response = await getAddressByPostalCode(formData.postalCode);
      
      if (response.data && response.data.length > 0) {
        const addressData = response.data[0];
        
        setFormData(prev => ({
          ...prev,
          prefecture: addressData.prefecture,
          city: addressData.city || '',
          street: addressData.town || ''
        }));
        
        setAddressFieldsChanged(true);
      } else {
        setErrors(prev => ({
          ...prev,
          postalCode: t('purchase.address.postal_code_not_found')
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        postalCode: t('purchase.address.postal_code_error')
      }));
    } finally {
      setIsAutoFilling(false);
    }
  };

  const addressOptions = [
    { value: 'new', label: t('purchase.delivery_info.create_new_address') },
    ...addresses.map(address => ({
      value: address.id,
      label: `${address.lastname} ${address.firstname} ${address.contactNumber} ${address.postalCode} ${address.prefecture} ${address.city} ${address.address1}`
    })),
  ];

  if (!isEditing) {
    return (
      <div className="bg-white rounded-md">
        <div className="py-6 px-6 flex justify-between items-center">
          <div className="flex items-center justify-center gap-2">
            <h2 className="text-lg font-bold">{t('purchase.delivery_info.title')}</h2>
            <img src={markedIcon} className="w-5 h-5" alt="" />
          </div>
          <button
            onClick={() => {
              if (onEditRequest && !onEditRequest()) {
                return; // Don't allow edit if other section is editing
              }
              setIsEditing(true);
            }}
            className="px-3.5 py-1 rounded-3xl border border-[#111111] text-xs"
          >
            {t('common.edit')}
          </button>
        </div>
        <div className="px-6 pb-6">
          <div className="flex items-center gap-2">
            <div className="text-sm">
              {formData.lastName} {formData.firstName}<br/>
              {formData.lastNameKana} {formData.firstNameKana}<br/>
              {formData.postalCode}<br/>
              {formData.prefecture} {formData.city} {formData.street} {formData.building && `${formData.building}`}<br/>
              {formData.phone}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise, show the editing view
  return (
    <div className="bg-white rounded-md">
      <div className="py-6 px-6">
        <h2 className="text-lg font-bold">{t('purchase.delivery_info.title')}</h2>
      </div>
      <div className="px-6 pb-6">
        {addresses.length > 0 && (
          <div className="space-y-3">
            <div className="text-sm font-bold text-[#111111] mb-3">{t('purchase.delivery_info.saved_addresses')}</div>
            <div className="mb-6">
              <Select
                options={addressOptions}
                value={isCreatingNew ? 'new' : selectedAddressId}
                onValueChange={handleAddressSelect}
                className="w-full h-12 rounded-md text-start"
              />
            </div>
          </div>
        )}

        <div className="space-y-3 mt-4">
          <div className="text-sm font-bold text-[#111111] mb-3">{t('purchase.delivery_info.personal_info')}</div>
          <div className="space-y-3">
            <div className="flex gap-4">
              <BasicInput
                title={t('purchase.delivery_info.last_name')}
                placeholder={t('purchase.delivery_info.last_name_placeholder')}
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                containerClassName="flex-1"
                error={errors.lastName}
              />
              <BasicInput
                title={t('purchase.delivery_info.first_name')}
                placeholder={t('purchase.delivery_info.first_name_placeholder')}
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                containerClassName="flex-1"
                error={errors.firstName}
              />
            </div>

            <div className="flex gap-4">
              <BasicInput
                title={t('purchase.delivery_info.last_name_kana')}
                placeholder={t('purchase.delivery_info.last_name_kana_placeholder')}
                value={formData.lastNameKana}
                onChange={(e) => handleInputChange('lastNameKana', e.target.value)}
                containerClassName="flex-1"
                error={errors.lastNameKana}
              />
              <BasicInput
                title={t('purchase.delivery_info.first_name_kana')}
                placeholder={t('purchase.delivery_info.first_name_kana_placeholder')}
                value={formData.firstNameKana}
                onChange={(e) => handleInputChange('firstNameKana', e.target.value)}
                containerClassName="flex-1"
                error={errors.firstNameKana}
              />
            </div>

            <div className="flex gap-4">
              <BasicInput
                title={t('purchase.delivery_info.phone')}
                placeholder={t('purchase.delivery_info.phone_placeholder')}
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                containerClassName="flex-1"
                error={errors.phone}
              />
              <div className="flex-1"></div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="text-sm font-bold text-[#111111] mb-3 flex items-center gap-2">
            <div>{t('purchase.address.title')}</div>
            {addressFieldsChanged && (
              <div className="text-[#3b5493] text-sm font-normal">{t('purchase.address.verify_note')}</div>
            )}
          </div>
          <div className="space-y-3">
            <div className="flex gap-4">
              <BasicInput
                title={t('purchase.address.postal_code')}
                placeholder={t('purchase.address.postal_code_placeholder')}
                value={formData.postalCode}
                onChange={(e) => handleInputChange('postalCode', e.target.value)}
                prefix="〒"
                prefixClassName="text-black"
                containerClassName="flex-1"
                error={errors.postalCode}
              >
                <div className={`absolute right-0 top-0 h-full bg-white border rounded-l-none rounded-r-md ${errors.postalCode ? 'border-[#ff5501]' : 'border-[#dddddd]'}`}>
                  <button 
                    className="h-full px-3 py-2.5 text-[#2966ff] text-sm"
                    onClick={handleAutoFill}
                    disabled={isAutoFilling || !formData.postalCode}
                  >
                    {isAutoFilling ? t('purchase.address.auto_filling') : t('purchase.address.auto_fill')}
                  </button>
                </div>
              </BasicInput>
              <div className="flex-1">
                <div className="text-sm text-[#111111] mb-1">{t('purchase.address.prefecture')}</div>
                <Select
                  options={prefectures}
                  value={formData.prefecture}
                  onValueChange={(value) => handleInputChange('prefecture', value)}
                  className="w-full h-12 text-start !rounded-md"
                  placeholder={t('purchase.address.prefecture_placeholder')}
                  error={errors.prefecture}
                  disabled={isLoadingPrefectures}
                  maxVisibleItems={8} // Show up to 8 prefectures at once
                />
              </div>
            </div>

            <div className="flex gap-4">
              <BasicInput
                title={t('purchase.address.city')}
                placeholder={t('purchase.address.city_placeholder')}
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                containerClassName="flex-1"
                error={errors.city}
              />
              <BasicInput
                title={t('purchase.address.street')}
                placeholder={t('purchase.address.street_placeholder')}
                value={formData.street}
                onChange={(e) => handleInputChange('street', e.target.value)}
                containerClassName="flex-1"
                error={errors.street}
              />
            </div>

            <div className="flex gap-4">
              <BasicInput
                title={t('purchase.address.building')}
                placeholder={t('purchase.address.building_placeholder')}
                value={formData.building}
                onChange={(e) => handleInputChange('building', e.target.value)}
                containerClassName="flex-1"
                error={errors.building}
              />
              <div className="flex-1"></div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button 
            onClick={handleContinue}
            variant="primary"
            size="l"
            disabled={!isFormAllFilled}
            className="h-12 px-6 bg-[#111111] text-white rounded-3xl disabled:bg-[#eeeeee] disabled:text-[#aaaaaa]"
          >
            {addresses.length > 0 ? t('purchase.delivery_info.save') : t('purchase.delivery_info.continue')}
          </Button>
        </div>
      </div>

      {/* Address Limit Dialog */}
      <ConfirmDialog
        isOpen={isLimitDialogOpen}
        onClose={handleLimitDialogClose}
        onConfirm={handleLimitDialogClose}
        title={t('address.limitReached')}
        description={t('address.limitDescription')}
        confirmText={t('common.ok')}
      />
    </div>
  );
};

export default AddressSection; 